import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { useSettingInsiderPhoneSuppportEnabled } from "@faire/retailer-visitor-shared/settings/useSettingInsiderPhoneSuppportEnabled";
import { InsiderBenefitsOverview } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/InsiderBenefitsOverview/InsiderBenefitsOverview";
import { InsiderBenefitsOverviewItem } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/InsiderBenefitsOverview/InsiderBenefitsOverviewItem";
import { RenderWithBuilder } from "@faire/web--source/builder/BuilderComponents/RenderWithBuilder";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useFormatMoney } from "@faire/web--source/common/money";
import { Global } from "@faire/web--source/slate/icons/foundation/Global";
import { Phone } from "@faire/web--source/slate/icons/foundation/Phone";
import { Shipping } from "@faire/web--source/slate/icons/foundation/Shipping";
import { ShoppingBag } from "@faire/web--source/slate/icons/foundation/ShoppingBag";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Money } from "@faire/web--source/ui/Money";
import React from "react";
import styled from "styled-components";
export var InsiderBenefitsOverviewRenderer = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var formatMoney = useFormatMoney().formatMoney;
    var hasFreeDutyAdvertising = useInsiderFeatureEligibility().hasFreeDutyAdvertising;
    var isInsiderPhoneSupportEnabled = useSettingInsiderPhoneSuppportEnabled();
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, thresholdValue = _useInsiderMembershipInformation_thresholds.threshold1;
    if (isInsiderPhoneSupportEnabled) {
        return /*#__PURE__*/ _jsx(RenderWithBuilder, {
            modelName: "insider-marketing-page-benefits-section",
            data: {
                thresholdValue: thresholdValue ? formatMoney({
                    money: thresholdValue,
                    omitCentsIfZero: true
                }) : undefined,
                hasFreeDutyAdvertising: hasFreeDutyAdvertising
            }
        });
    }
    return /*#__PURE__*/ _jsx(InsiderBenefitsOverview, {
        title: strictLocalize({
            id: "tJ7II/",
            defaultMessage: [
                {
                    type: 0,
                    value: "Exclusive membership benefits"
                }
            ]
        }),
        children: hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(EuropeanInsiderBenefitsOverviewContent, {}) : /*#__PURE__*/ _jsx(USInsiderBenefitsOverviewContent, {})
    });
};
var USInsiderBenefitsOverviewContent = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var hasPhoneSupport = useInsiderFeatureEligibility().hasPhoneSupport;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(InsiderBenefitsOverviewItem, {
                icon: /*#__PURE__*/ _jsx(Shipping, {
                    fontSize: "24px"
                }),
                title: strictLocalize({
                    id: "SlhNYU",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Free shipping"
                        }
                    ]
                }),
                description: /*#__PURE__*/ _jsx(InsiderFreeShippingDescription, {})
            }),
            /*#__PURE__*/ _jsx(InsiderEarlyAccessOverviewItem, {}),
            hasPhoneSupport ? /*#__PURE__*/ _jsx(InsiderBenefitsOverviewItem, {
                icon: /*#__PURE__*/ _jsx(Phone, {
                    fontSize: "24px"
                }),
                title: strictLocalize({
                    id: "OSoFsV",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Faire phone support"
                        }
                    ]
                }),
                description: strictLocalize({
                    id: "jd6rAh",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Request a call from a Faire team member to talk through issues or ask questions."
                        }
                    ]
                })
            }) : null
        ]
    });
};
var EuropeanInsiderBenefitsOverviewContent = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, thresholdValue = _useInsiderMembershipInformation_thresholds.threshold1;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(InsiderBenefitsOverviewItem, {
                icon: /*#__PURE__*/ _jsx(Shipping, {
                    fontSize: "24px"
                }),
                title: strictLocalize({
                    id: "wyhDsG",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Free shipping on thousands of brands"
                        }
                    ]
                }),
                description: /*#__PURE__*/ _jsx(InsiderFreeShippingDescription, {})
            }),
            /*#__PURE__*/ _jsx(InsiderBenefitsOverviewItem, {
                icon: /*#__PURE__*/ _jsx(Global, {
                    fontSize: "24px"
                }),
                title: strictLocalize({
                    id: "dJvcVy",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Free import duties on select orders"
                        }
                    ]
                }),
                description: thresholdValue ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "7uEsY2",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Save time and money with import duties covered on thousands of international brands. Some require an order of "
                        },
                        {
                            type: 1,
                            value: "freeShippingThresholdMoney"
                        },
                        {
                            type: 0,
                            value: " to qualify."
                        }
                    ],
                    values: {
                        freeShippingThresholdMoney: /*#__PURE__*/ _jsx(Money, {
                            money: thresholdValue,
                            omitCentsIfZero: true
                        })
                    }
                }) : strictLocalize({
                    id: "5PqeJZ",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Save time and money with import duties covered on thousands of international brands."
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(InsiderEarlyAccessOverviewItem, {})
        ]
    });
};
var InsiderEarlyAccessOverviewItem = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    return /*#__PURE__*/ _jsx(InsiderBenefitsOverviewItem, {
        icon: /*#__PURE__*/ _jsx(ShoppingBag, {
            fontSize: "24px"
        }),
        title: strictLocalize({
            id: "vz4m6T",
            defaultMessage: [
                {
                    type: 0,
                    value: "Early access to our virtual trade shows"
                }
            ]
        }),
        description: strictLocalize({
            id: "sveRlo",
            defaultMessage: [
                {
                    type: 0,
                    value: "Start shopping the best deals of the year before other retailers on Faire."
                }
            ]
        })
    });
};
var InsiderFreeShippingDescription = function() {
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, thresholdValue = _useInsiderMembershipInformation_thresholds.threshold1;
    var ignoreThresholdAdvertising = useInsiderFeatureEligibility().ignoreThresholdAdvertising;
    return thresholdValue && !ignoreThresholdAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "xzEYwU",
        defaultMessage: [
            {
                type: 0,
                value: "Look for the Insider key "
            },
            {
                type: 1,
                value: "insiderToken"
            },
            {
                type: 0,
                value: " to find products with free shipping. Some require an order of "
            },
            {
                type: 1,
                value: "freeShippingThresholdMoney"
            },
            {
                type: 0,
                value: " to qualify."
            }
        ],
        values: {
            insiderToken: /*#__PURE__*/ _jsx(StyledInsider, {
                viewBox: "0 0 14 12",
                small: true
            }),
            freeShippingThresholdMoney: /*#__PURE__*/ _jsx(Money, {
                money: thresholdValue,
                omitCentsIfZero: true
            })
        }
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "fI8Sx0",
        defaultMessage: [
            {
                type: 0,
                value: "Look for the Insider key "
            },
            {
                type: 1,
                value: "insiderToken"
            },
            {
                type: 0,
                value: " to find products with free shipping."
            }
        ],
        values: {
            insiderToken: /*#__PURE__*/ _jsx(StyledInsider, {
                viewBox: "0 0 14 12",
                small: true
            })
        }
    });
};
var StyledInsider = styled(Insider).withConfig({
    componentId: "sc-a70c4a10-0"
})([
    "vertical-align:baseline;"
]);
